/**
 * Server state, one hook per resource. Screens import from here and never
 * call the API modules directly for reads, so "where does this screen get its
 * data" is always answered by grepping `use…` here.
 *
 * Polling follows the contract: async resources carry `status` and are polled
 * at ≤ 1/s (tailor runs), ≤ every 1.5 s (guest analyses) until terminal.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData, type QueryClient } from '@tanstack/react-query';
import { actionApi } from './action';
import { applicationsApi, listAllApplications } from './applications';
import { authApi } from './auth';
import { autoApplyApi } from './autoApply';
import { billingApi } from './billing';
import { certificationsApi } from './certifications';
import { ApiError, GAP_RETRIES, gapRetryDelay, isApiError, isGapError, newIdempotencyKey } from './client';
import { deckApi } from './deck';
import { guestApi } from './guest';
import { insightsApi } from './insights';
import { learningApi, type LearningItemSet, type LearningOwnAdd } from './learning';
import { isLearningPathBuilding } from '../lib/learning';
import { jobsApi } from './jobs';
import { meApi } from './me';
import { resumesApi } from './resumes';
import { answersApi } from './action';
import { profileApi } from './profile';
import type { ActionQuestion, AnswerValue, CertificationStepValue, ProfileLocationPut, ProfileResponse, TailorDefaultLevel } from './types';
import { supportApi } from './support';
import { isTerminal, tailorApi } from './tailor';
import { DECK_BATCH, DECK_PREFETCH_AT, DECK_STALE_MS } from '../lib/deck';
import type { ActionResponse, ActionRow, ActionSummary, Application, ApplicationCreateRequest, ApplicationStatus, AutoApplyStrategy, DeckCard, DeckDecisionKind, DeckPassReason, DeckRequirements, DeckRequirementsPut, DeckResponse, Insights, InsightsGate, JobApplicationRef, JobDetailResponse, JobListQuery, JobSkillAddRequest, GapQuestions, LearningOwnItem, LearningPath, LegalDocumentKey, MePatch, ResumePatch, TailorRun } from './types';

export const qk = {
  me: ['me'] as const,
  credits: ['credits'] as const,
  ledger: (page: number) => ['credits', 'ledger', page] as const,
  billing: ['billing'] as const,
  resumes: (kind: string) => ['resumes', kind] as const,
  resume: (id: string) => ['resume', id] as const,
  /** With a New Health Check's role the report is a different row, so a different key; without one it is the account's role's, shared by every reader (Insights, Studio). A prefix match on `['resume', id, 'health']` still invalidates both. */
  resumeHealth: (id: string, role?: string) => (role ? (['resume', id, 'health', role] as const) : (['resume', id, 'health'] as const)),
  upload: (id: string) => ['upload', id] as const,
  jobs: (q: JobListQuery) => ['jobs', q] as const,
  jobPlaces: (q: string) => ['job-places', q] as const,
  guestRoles: (q: string) => ['guest-roles', q] as const,
  job: (id: string) => ['job', id] as const,
  deck: ['deck'] as const,
  deckRequirements: ['deckRequirements'] as const,
  /** Action's list (`useAction`). Its kits and questions sit under the same prefix, so invalidating `action` after an answer reaches all three (`refreshAction`). */
  action: ['action'] as const,
  actionKit: (jobId: string) => ['action', 'kit', jobId] as const,
  actionQuestions: ['action', 'questions'] as const,
  answers: ['answers'] as const,
  profile: ['profile'] as const,
  tailorRun: (id: string) => ['tailorRun', id] as const,
  tailorHistory: ['tailorRuns'] as const,
  gapQuestions: (runId: string) => ['gapQuestions', runId] as const,
  tailorPreview: (jobId: string, resumeId: string) => ['tailorPreview', jobId, resumeId] as const,
  applications: ['applications'] as const,
  manualWeekly: ['stats', 'manualWeekly'] as const,
  autoApply: ['autoApply'] as const,
  insights: ['insights'] as const,
  learningPath: ['learning-path'] as const,
  guestAnalysis: (id: string) => ['guest', 'analysis', id] as const,
  authProviders: ['auth', 'providers'] as const,
};

// ── sign-in providers ────────────────────────────────────────────────────
/**
 * Which of Google and Apple the server can complete (OAuth ticket 01). A failed
 * ask is `undefined` and the sign-in screen draws no provider button: offering
 * one the server may not be able to finish is the defect this exists to close.
 * Asked once per visit; a redeploy that switches a provider is seen on the next.
 */
export const useAuthProviders = (enabled = true) =>
  useQuery({ queryKey: qk.authProviders, queryFn: authApi.providers, enabled, staleTime: 5 * 60_000, retry: false });

// ── me / credits / billing ───────────────────────────────────────────────
export const useCredits = () => useQuery({ queryKey: qk.credits, queryFn: billingApi.credits, staleTime: 30_000 });
export const useLedger = (page: number) => useQuery({ queryKey: qk.ledger(page), queryFn: () => billingApi.ledger(page, 8), placeholderData: keepPreviousData });
export const useBilling = () => useQuery({ queryKey: qk.billing, queryFn: billingApi.billing, staleTime: 30_000 });

export function usePatchMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MePatch) => meApi.patch(body),
    onSuccess: (me) => qc.setQueryData(qk.me, me),
  });
}

/**
 * `POST /v1/me/legal-acceptances` (legal-pages tickets 03 and 07). The reply is
 * the `me` shape, whose `legal.termsVersions` now holds the version just posted
 * whether or not this call wrote the row; it replaces the cached `me`, which
 * `AuthProvider` follows, and that is what closes the agreement step.
 */
export function useAcceptLegal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { document: LegalDocumentKey; version: string }) => meApi.acceptLegal(body),
    onSuccess: (me) => qc.setQueryData(qk.me, me),
  });
}

/** 202: every session is already revoked, so there is nothing to invalidate; the caller ends the local session. */
export const useDeleteAccount = () => useMutation({ mutationFn: meApi.deleteAccount });
/** "Contact us" (ux-bug-pass ticket 29): stores and emails one message. Nothing cached reads it, so nothing to invalidate. */
export const useSupportMessage = () => useMutation({ mutationFn: supportApi.send });

/** After any credit-affecting operation the balance is re-read, never computed locally. */
export function invalidateCredits(qc: QueryClient) {
  return Promise.all([qc.invalidateQueries({ queryKey: qk.credits }), qc.invalidateQueries({ queryKey: ['credits', 'ledger'] })]);
}

// ── resumes ──────────────────────────────────────────────────────────────
export const useResumes = (kind: 'user' | 'tailored' | 'all' = 'user', enabled = true) => useQuery({ queryKey: qk.resumes(kind), queryFn: () => resumesApi.list(kind), staleTime: 15_000, enabled });
/**
 * Whether the signed-in account has a master résumé (ux-bug-pass-2 ticket 11):
 * the one fact the funnel's "Fix My Resume" and the sign-in gate decide on, and
 * the server's own definition of "no résumé" (`resumes.master`). `hasMaster` is
 * `undefined` until it is known and for good if the list fails to load, which
 * every caller reads as "keep today's behaviour"; `loading` is only the first
 * fetch. Off (signed out) it reads nothing.
 */
export function useHasMasterResume(enabled: boolean): { hasMaster: boolean | undefined; loading: boolean } {
  const list = useResumes('all', enabled);
  return { hasMaster: list.data ? list.data.items.some((r) => r.isMaster) : undefined, loading: enabled && list.isLoading };
}
export const useResume = (id: string | null | undefined) => useQuery({ queryKey: qk.resume(id ?? ''), queryFn: () => resumesApi.get(id!), enabled: !!id });
/**
 * A poll that failed on a server gap (502, 503, 504, no connection) keeps going once the retries ran out, but only
 * when it holds a body: TanStack keeps the error on screen until the next read succeeds, so nothing flickers. With
 * no body the first read is still owned by the screen's error and its "Try again" (zero-downtime-deploys ticket 03).
 */
const resumesAfterGap = (s: { error: unknown; data: unknown }) => isGapError(s.error) && s.data !== undefined;
/** Resume Health's poll interval while suggestions are `pending`. `scripts/flows.mjs`
 *  reads it from here, so its "no more reads" waits outlast one poll. */
export const HEALTH_POLL_MS = 2000;
/**
 * `untilSuggestionsReady` polls while a Pro account's rewrite suggestions are
 * `pending`, the one state the server moves on its own (resume-360 ticket 24).
 * Only Resume Health, which draws them, asks; the other readers want the score.
 */
export const useResumeHealth = (id: string | null | undefined, { untilSuggestionsReady = false, targetRole }: { untilSuggestionsReady?: boolean; targetRole?: string } = {}) =>
  useQuery({
    queryKey: qk.resumeHealth(id ?? '', targetRole),
    queryFn: () => resumesApi.health(id!, false, targetRole),
    enabled: !!id,
    staleTime: 60_000,
    // A poll that has errored stays errored, as `useGuestAnalysis`'s does.
    // After a failed refetch TanStack keeps the last body in `state.data`, so
    // a check on the body alone reads `pending` and asks again every interval.
    refetchInterval: (q) => {
      if (q.state.status === 'error' && !resumesAfterGap(q.state)) return false;
      return untilSuggestionsReady && q.state.data?.aiSuggestionsStatus === 'pending' ? HEALTH_POLL_MS : false;
    },
  });
export const useUpload = (id: string | null | undefined) =>
  useQuery({
    queryKey: qk.upload(id ?? ''),
    queryFn: () => resumesApi.upload(id!),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data && (q.state.data.status === 'parsed' || q.state.data.status === 'failed') ? false : 1500),
  });

export function invalidateResumes(qc: QueryClient, id?: string) {
  // Insights too: adding a first résumé is half of the Career Compass unlock
  // gate, and `useInsights` has a 60 s staleTime — without this, someone who
  // builds a résumé and goes straight to Insights is served a cached body still
  // saying "Add your resume", which is exactly the lag the server-side live
  // gate exists to avoid.
  const ps: Promise<unknown>[] = [
    qc.invalidateQueries({ queryKey: ['resumes'] }),
    qc.invalidateQueries({ queryKey: qk.insights }),
    // The learning path is built from the master résumé's gaps, so a new or edited résumé makes it stale.
    qc.invalidateQueries({ queryKey: qk.learningPath }),
  ];
  if (id) ps.push(qc.invalidateQueries({ queryKey: qk.resume(id) }));
  return Promise.all(ps);
}

// ── jobs ─────────────────────────────────────────────────────────────────
/**
 * The place search's suggestions (ux-bug-pass-7 ticket 05). One cache entry per typed text, so a stale answer can
 * never stand in for a newer one; React Query aborts a request whose text was replaced before it answered.
 */
/** The target role field's suggestions (onboarding, 2026-10-22); a failure falls back to the fixed list in the field. */
export const useRoleSuggestions = (q: string, enabled: boolean) => useQuery({ queryKey: qk.guestRoles(q), queryFn: ({ signal }) => guestApi.roles(q, signal), enabled, placeholderData: keepPreviousData, staleTime: 10 * 60_000, retry: false });
/** The role field's first list, asked as /start opens (2026-11-03): the server reads its role index on a process's
 *  first ask, so asking while the person picks a file means typing finds it ready. A failure is the field's own fallback. */
export const prefetchRoleSuggestions = (qc: QueryClient) => qc.prefetchQuery({ queryKey: qk.guestRoles(''), queryFn: ({ signal }) => guestApi.roles('', signal), staleTime: 10 * 60_000, retry: false });
export const useJobPlaces = (q: string, enabled: boolean) => useQuery({ queryKey: qk.jobPlaces(q), queryFn: ({ signal }) => jobsApi.locations(q, signal), enabled, placeholderData: keepPreviousData, staleTime: 5 * 60_000, retry: false });
export const useJobs = (q: JobListQuery) => useQuery({ queryKey: qk.jobs(q), queryFn: () => jobsApi.list(q), placeholderData: keepPreviousData, staleTime: 30_000 });
export const useJob = (id: string | null | undefined) => useQuery({ queryKey: qk.job(id ?? ''), queryFn: () => jobsApi.get(id!), enabled: !!id, staleTime: 15_000 });

// ── the deck (swipe-deck tickets 05 and 13) ──────────────────────────────
/**
 * How often the deck's counters are re-read while an application is still being prepared
 * (`preparingToday > 0`), so the header's "N ready" pill follows the run the apply started without a
 * reload. Slower than a run's own poll: nothing on the deck draws the run's steps.
 */
export const DECK_PREPARING_POLL_MS = 3000;

/**
 * The deck as the cache holds it (ticket 13): the last `GET /v1/deck` answer, with `items` standing for
 * every undecided card the screen has in hand (the first batch and each one appended after it),
 * `nextCursor` for the batch after the last of them, and `total` kept current locally.
 */
export type DeckHeld = DeckResponse;

// Every deck write shares this key, so "is a decision still in flight" is one question.
const DECK_WRITE = ['deck', 'write'] as const;
// Bumped by every local edit to the held deck; and the number of writes sent and not yet answered. A counters read
// is applied only if neither moved while it was on the wire: otherwise it describes the deck from before the
// person's last swipe and would put `total` back up under their thumb. That swipe's own read follows.
let deckEdits = 0;
let deckWrites = 0;

/**
 * Reads the counters and `total` alone (`limit=0`: no card is hydrated) and writes them into the held
 * deck. Never touches `items`: replacing the list under the person's thumb is the thing batching avoids.
 */
export async function refreshDeckSummary(qc: QueryClient): Promise<void> {
  if (!qc.getQueryData(qk.deck) || deckWrites > 0) return;
  const edits = deckEdits;
  let s: DeckResponse;
  try {
    s = await deckApi.get({ limit: 0 });
  } catch {
    return; // the next decision, or the next poll, asks again
  }
  if (edits !== deckEdits || deckWrites > 0) return;
  qc.setQueryData<DeckHeld>(qk.deck, (d) =>
    d && { ...d, total: s.total, hasMaster: s.hasMaster, freeLeft: s.freeLeft, preparingToday: s.preparingToday, readyToday: s.readyToday, sentToday: s.sentToday });
}

/** `page` after the cards already held: any id already held is dropped, the cursor moves on. */
function appendBatch(d: DeckHeld, page: DeckResponse, cursor: string): DeckHeld {
  const have = new Set(d.items.map((c) => c.id));
  const fresh = page.items.filter((c) => !have.has(c.id));
  // A server that answers no cards, or the cursor it was given, has nothing more to give: do not ask again.
  const end = page.items.length === 0 || page.nextCursor === cursor;
  return { ...d, items: [...d.items, ...fresh], nextCursor: end ? null : page.nextCursor };
}

/**
 * Today's shortlist, `limit=DECK_BATCH` cards at a time (ticket 13). The first read is the first batch;
 * `loadMore` appends the next. The held deck is trusted for `DECK_STALE_MS` (a job page and Back keep the
 * cards and the position); past that, mounting reads the first batch again, which is the same deck.
 * Nothing here refetches on its own after a decision: a decision edits the held list and asks for
 * `limit=0` (`refreshDeckSummary`).
 *
 * The next batch is fetched when `DECK_PREFETCH_AT` or fewer cards are held and there is a `nextCursor`
 * and no fetch is in flight. A fetch that fails leaves the deck as it was; it is tried again the next time
 * a card leaves (or on `retryMore`), never in a loop.
 */
export function useDeck() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: qk.deck,
    queryFn: (): Promise<DeckHeld> => deckApi.get({ limit: DECK_BATCH }),
    staleTime: DECK_STALE_MS,
  });
  const data = q.data;

  // While an application is being prepared the pill must follow the run: re-read the counters, not the cards.
  const preparing = (data?.preparingToday ?? 0) > 0;
  useEffect(() => {
    if (!preparing) return;
    const t = window.setInterval(() => { void refreshDeckSummary(qc); }, DECK_PREPARING_POLL_MS);
    return () => window.clearInterval(t);
  }, [preparing, qc]);

  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<unknown>(null);
  const inflight = useRef<Promise<void> | null>(null);
  // Which (cursor, cards held) a fetch failed at: the effect below does not try that again until a card leaves.
  const failedAt = useRef<string | null>(null);

  const loadMore = useCallback((): Promise<void> => {
    if (inflight.current) return inflight.current;
    const cur = qc.getQueryData<DeckHeld>(qk.deck);
    const cursor = cur?.nextCursor;
    if (!cur || !cursor) return Promise.resolve();
    setLoadingMore(true);
    setMoreError(null);
    const p = deckApi
      .get({ limit: DECK_BATCH, cursor })
      // Only if the held deck is still the one the cursor belongs to: a first batch read in the meantime has replaced it.
      .then((page) => { qc.setQueryData<DeckHeld>(qk.deck, (d) => (d && d.nextCursor === cursor ? appendBatch(d, page, cursor) : d)); })
      .catch((e) => {
        const d = qc.getQueryData<DeckHeld>(qk.deck);
        failedAt.current = cursor + '|' + (d?.items.length ?? 0);
        setMoreError(e);
      })
      .finally(() => { inflight.current = null; setLoadingMore(false); });
    inflight.current = p;
    return p;
  }, [qc]);

  const held = data?.items.length;
  const cursor = data?.nextCursor ?? null;
  useEffect(() => {
    if (held === undefined || cursor === null || held > DECK_PREFETCH_AT || loadingMore) return;
    if (failedAt.current === cursor + '|' + held) return;
    void loadMore();
  }, [held, cursor, loadingMore, loadMore]);

  /** "Try again" on the error the screen draws when the cards ran out with the next batch not fetched. */
  const retryMore = useCallback(() => { failedAt.current = null; void loadMore(); }, [loadMore]);

  return {
    data,
    isPending: q.isPending,
    /** The first batch could not be read and nothing is held. A failed refresh of a held deck is not this. */
    isError: q.isError && !data,
    error: q.error,
    refetch: q.refetch,
    /** The next batch is on its way. */
    loadingMore,
    /** The last fetch of the next batch failed (the error, else null). */
    moreError,
    retryMore,
  };
}
/**
 * What a decision changed besides the held deck (which it edits in place, and whose counters and `total`
 * `endDeckWrite` re-reads with `limit=0`): Recommended (a pass leaves it), and on an apply the balance and
 * Tailoring history. `qk.deck` itself is not invalidated: that would read the first batch again and replace
 * the list under the person's thumb. Action's list is read again: an apply adds a `preparing` row, a save a
 * `saved` one, and a pass takes a saved row away (the rail's badge and the deck's pill follow it).
 */
function afterDecision(qc: QueryClient, decision: DeckDecisionKind) {
  const ps: Promise<unknown>[] = [qc.invalidateQueries({ queryKey: ['jobs'] }), refreshAction(qc)];
  if (decision === 'apply') ps.push(invalidateCredits(qc), qc.invalidateQueries({ queryKey: qk.tailorHistory }));
  return Promise.all(ps);
}
/**
 * The held deck without `card`, which has been decided. Any decision takes the job out of the pool, so
 * `total` drops by one. That includes a save since 2026-10-10: a saved job is a generic application in Job
 * Queue and the deck does not deal it again (swipe-deck ticket 14).
 */
function leaveDeck(d: DeckHeld | undefined, card: DeckCard): DeckHeld | undefined {
  if (!d) return d;
  return { ...d, items: d.items.filter((c) => c.id !== card.id), total: Math.max(0, d.total - 1) };
}
/** The inverse of `leaveDeck`: `card` back on top (an undo, a refused decision). */
function returnToDeck(d: DeckHeld | undefined, card: DeckCard): DeckHeld | undefined {
  if (!d) return d;
  return { ...d, items: [card, ...d.items.filter((c) => c.id !== card.id)], total: d.total + 1 };
}
/** Brackets a deck write: the edit that `onMutate` makes at once, and the end of it (`onSuccess` / `onError`) of both mutations below. */
async function beginDeckWrite(qc: QueryClient, edit: (d: DeckHeld | undefined) => DeckHeld | undefined) {
  deckWrites += 1;
  deckEdits += 1;
  await qc.cancelQueries({ queryKey: qk.deck, exact: true });
  qc.setQueryData<DeckHeld>(qk.deck, edit);
}
function endDeckWrite(qc: QueryClient, rollback?: (d: DeckHeld | undefined) => DeckHeld | undefined) {
  deckWrites = Math.max(0, deckWrites - 1);
  if (rollback) { deckEdits += 1; qc.setQueryData<DeckHeld>(qk.deck, rollback); }
  // Success or refusal, the counters are asked for (the last write in flight is the one whose read is applied).
  void refreshDeckSummary(qc);
}
/**
 * `POST /v1/deck/{jobId}`. Optimistic: the card leaves the held deck before the server answers and
 * comes back on an error — a 402 included, which the screen answers by recording a `save` for the very card
 * that was refused (the job is then queued on the generic résumé). One request, whatever the card was: the server replaces a `save` with a `pass` or an
 * `apply` itself, after its own checks, so a 402 leaves the save where it was (a DELETE sent first would
 * have lost it). One `Idempotency-Key` per call: an apply spends a credit, and a retried tap is a new
 * attempt.
 */
export function useDeckDecide() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: DECK_WRITE,
    mutationFn: ({ card, decision }: { card: DeckCard; decision: DeckDecisionKind }) => deckApi.decide(card.id, decision, newIdempotencyKey()),
    onMutate: ({ card }) => beginDeckWrite(qc, (d) => leaveDeck(d, card)),
    onError: (_e, { card }) => endDeckWrite(qc, (d) => returnToDeck(d, card)),
    onSuccess: (_d, { decision }) => { endDeckWrite(qc); void afterDecision(qc, decision); },
  });
}
/**
 * `POST /v1/deck/{jobId}` from outside the deck (find-jobs 01: "Tailor & Apply" on Job Search and the job page). The
 * server takes any publishable job, so this is the right swipe for a job the deck may never have dealt. Not optimistic:
 * there is no held card to move. On success the job leaves the held deck, if it was there, and what a decision moves is
 * read again. One `Idempotency-Key` per call, as the deck's.
 */
export function useJobQueueDecide() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, decision }: { jobId: string; decision: DeckDecisionKind }) => deckApi.decide(jobId, decision, newIdempotencyKey()),
    onSuccess: (_d, { jobId, decision }) => {
      qc.setQueryData<DeckHeld>(qk.deck, (d) => { const card = d?.items.find((c) => c.id === jobId); return card ? leaveDeck(d, card) : d; });
      void refreshDeckSummary(qc);
      void afterDecision(qc, decision);
    },
  });
}
/** The Deck's Hard requirements (ticket 14); asked only once the deck itself shows the ranking is on. */
export const useDeckRequirements = (enabled: boolean) => useQuery({ queryKey: qk.deckRequirements, queryFn: deckApi.requirements, enabled, staleTime: 60_000 });
/**
 * `PUT /v1/deck/requirements`. The requirements are the Profile page's location and work arrangement rows, and they
 * decide the pool, so a save re-reads the requirements, the Profile page and the deck (the next batch is the new one).
 */
export function usePutDeckRequirements() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DeckRequirementsPut): Promise<DeckRequirements> => deckApi.putRequirements(body),
    onSuccess: (held) => {
      qc.setQueryData(qk.deckRequirements, held);
      void qc.invalidateQueries({ queryKey: qk.profile });
      void qc.invalidateQueries({ queryKey: qk.deck });
    },
  });
}
/** Whether a held card is one a pass's reason about `passed` hides: the same company, role, place or level. The server's rule is finer; this only keeps the cards in hand from showing what was just refused. */
function hiddenBy(passed: DeckCard, reason: DeckPassReason, c: DeckCard): boolean {
  if (reason === 'company') return !!passed.org && c.org === passed.org;
  if (reason === 'role') return c.role === passed.role;
  if (reason === 'location') return !!passed.location && c.location === passed.location;
  return !!passed.seniority && c.seniority === passed.seniority;
}
/**
 * `POST /v1/deck/{jobId}/reason` (shortlist-relevance 07). The requirements are read again; the held deck loses the
 * cards the reason hides and re-reads its counters, but is not read again (that would replace the list under the thumb).
 */
export function useDeckPassReason() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ card, reason }: { card: DeckCard; reason: DeckPassReason }) => deckApi.passReason(card.id, reason),
    onSuccess: (_answer, { card, reason }) => {
      void qc.invalidateQueries({ queryKey: qk.deckRequirements });
      qc.setQueryData<DeckHeld>(qk.deck, (d) => d && { ...d, items: d.items.filter((c) => !hiddenBy(card, reason, c)) });
      void refreshDeckSummary(qc);
    },
  });
}
/** `POST /v1/deck/taste/reset` (ticket 15). The held cards stay as dealt; the note is dropped from the held deck, and the next batch is read without Taste. */
export function useResetDeckTaste() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => deckApi.resetTaste(),
    onSuccess: () => { qc.setQueryData<DeckHeld>(qk.deck, (d) => d && { ...d, tasteNote: null }); },
  });
}
/** `DELETE /v1/deck/{jobId}`, a pass undone. Optimistic the other way: the card is back on top at once, and gone again if the server refuses. */
export function useDeckUndo() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: DECK_WRITE,
    mutationFn: (card: DeckCard) => deckApi.undo(card.id),
    onMutate: (card) => beginDeckWrite(qc, (d) => returnToDeck(d, card)),
    onError: (_e, card) => endDeckWrite(qc, (d) => leaveDeck(d, card)),
    onSuccess: () => { endDeckWrite(qc); void afterDecision(qc, 'pass'); },
  });
}

// ── Action (action-tab ticket 17) ────────────────────────────────────────
/** How often Action's list is read again while a loaded row is `preparing` (spec 8.1). `flows.mjs` reads this number from this file. */
export const ACTION_POLL_MS = 3000;
/**
 * Action's list, `GET /v1/action`, To do a page of 50 at a time. An infinite query, so that a re-read
 * (the poll, `refreshAction`, a stale mount) reads every page already loaded again, in order, and not only the first.
 * One entry, `qk.action`: the screen, the rail's badge and the deck's pill are observers of the same one.
 *
 * While any loaded row is `preparing` the list is read every `ACTION_POLL_MS`, so the row turns green, and the
 * badge counts it, with no reload. That timer belongs to the observer that asks for it (`poll`), and only the
 * shell does: it is mounted under every screen, and two observers polling would be two reads every 3 s.
 * With no row preparing nothing polls, and the list is read again when the tab is looked at again (off
 * app-wide, on here: a person comes back from the employer's page to this one).
 */
export function useAction(poll = false) {
  return useInfiniteQuery({
    queryKey: qk.action,
    queryFn: ({ pageParam }) => actionApi.list(pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last: ActionResponse) => last.nextCursor,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
    refetchInterval: poll ? (q) => (q.state.data?.pages.some((p) => p.todo.some((r) => r.state === 'preparing')) ? ACTION_POLL_MS : false) : false,
  });
}
/** The header's counts. `summary` (and `applied`) describe the whole list and are the same on every page, so they are read off the first. */
export const actionSummary = (d: InfiniteData<ActionResponse> | undefined): ActionSummary | null => d?.pages[0]?.summary ?? null;
/**
 * Reads Action again after anything that can move a row: the list (every loaded page), each kit and the open
 * questions, which share the `['action']` prefix. The sheets of tickets 18 to 20 call it after a write; a row
 * whose state it changes flashes on the screen with no more said.
 */
export const refreshAction = (qc: QueryClient) => qc.invalidateQueries({ queryKey: qk.action });

/** What a row's own button can ask for (spec 8.1). */
export type ActionRowWrite = 'start' | 'unlock' | 'retry' | 'remove';
/**
 * The four writes of a row's own button (spec 8.1, and 9.1's settled list). None is optimistic: the row moves
 * when the list is read again.
 *
 *   start   a `saved` row: `POST /v1/deck/{jobId}` with `apply`, the deck's own route, which replaces the save
 *   unlock  a `locked` row: `POST /v1/tailor-runs/{runId}/unlock`, as the job page does
 *   retry   a `failed` row: `POST /v1/action/{jobId}/retry`, a new run
 *   remove  a `closed` or `failed` row, or a pasted job's `ready` one: `DELETE /v1/action/{jobId}`
 *
 * The first three spend a credit (an `Idempotency-Key` where the route takes one) and answer 402
 * `insufficient_credits` with nothing written; the screen opens the upgrade sheet on that.
 */
export function useActionRowWrite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ kind, row }: { kind: ActionRowWrite; row: ActionRow }): Promise<TailorRun | null> => {
      if (kind === 'unlock') return row.runId ? tailorApi.unlock(row.runId) : null;
      if (kind === 'start') await deckApi.decide(row.jobId, 'apply', newIdempotencyKey());
      else if (kind === 'retry') await actionApi.retry(row.jobId, newIdempotencyKey());
      else await actionApi.remove(row.jobId);
      return null;
    },
    onSuccess: (unlocked, { kind }) => {
      if (unlocked) void invalidateAfterRun(qc, unlocked);
      else void refreshAction(qc);
      // The held deck still has the card a start has just used up, or the job a remove has just passed on:
      // marked stale, like `useApplicationMutations` does, and read again when the deck next mounts.
      if (kind === 'start' || kind === 'remove') { void qc.invalidateQueries({ queryKey: qk.deck }); void qc.invalidateQueries({ queryKey: ['jobs'] }); }
      if (kind === 'start' || kind === 'retry') { void invalidateCredits(qc); void qc.invalidateQueries({ queryKey: qk.tailorHistory }); }
    },
  });
}

// ── tailor runs ──────────────────────────────────────────────────────────
export const useTailorRun = (id: string | null | undefined) =>
  useQuery({
    queryKey: qk.tailorRun(id ?? ''),
    queryFn: () => tailorApi.run(id!),
    enabled: !!id,
    // 1.5 s like the upload and guest-analysis polls: the backend allows one
    // poll per run per second, and a 1 s timer that fires early would get a 429.
    refetchInterval: (q) => (isTerminal(q.state.data as TailorRun | undefined) ? false : 1500),
  });
export const useTailorHistory = (opts?: { enabled?: boolean }) => useQuery({ queryKey: qk.tailorHistory, queryFn: () => tailorApi.history(1, 30), staleTime: 15_000, enabled: opts?.enabled ?? true });
// ── a rebuild's Gap questions (compass-health-rebuild ticket 14) ─────────
/**
 * The questions and where the account got to, read from the server and nowhere
 * else. `staleTime: 0` because this **is** the
 * screen's state, and returning to it must show the next unanswered question.
 * `retry: false` on a 404, so a run that is not yours reads as not-found at
 * once rather than after three attempts.
 */
export const useGapQuestions = (runId: string | null | undefined) =>
  useQuery({
    queryKey: qk.gapQuestions(runId ?? ''),
    queryFn: () => tailorApi.questions(runId!),
    enabled: !!runId,
    staleTime: 0,
    retry: (n, e) => !(e instanceof ApiError && e.status >= 400 && e.status < 500) && n < 2,
  });

/**
 * One write per tap — **not optimistic**, so the bar never moves before the
 * server has taken the answer. Each write answers with the whole set, which is
 * put straight into the cache; `finish` answers with the run, which is put
 * into the run's own cache so the Rebuild page's poll starts from it.
 */
export function useGapQuestionMutations(runId: string) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const land = useCallback((q: GapQuestions) => {
    qc.setQueryData(qk.gapQuestions(runId), q);
    return q;
  }, [qc, runId]);
  const wrap = useCallback(async <T>(call: () => Promise<T>): Promise<T> => {
    setBusy(true);
    try { return await call(); } finally { setBusy(false); }
  }, []);
  const answer = useCallback((qid: string, text: string) => wrap(async () => land(await tailorApi.answerQuestion(runId, qid, text))), [wrap, land, runId]);
  const skip = useCallback((qid: string) => wrap(async () => land(await tailorApi.skipQuestion(runId, qid))), [wrap, land, runId]);
  const finish = useCallback(() => wrap(async () => {
    const run = await tailorApi.finishQuestions(runId);
    qc.setQueryData(qk.tailorRun(run.id), run);
    void qc.invalidateQueries({ queryKey: qk.gapQuestions(runId) });
    // History still holds this run as `asking`; the Rebuild page reads it to
    // show an in-flight run (ux-bug-pass ticket 21), not a "Waiting" one.
    void qc.invalidateQueries({ queryKey: qk.tailorHistory });
    void invalidateCredits(qc);
    return run;
  }), [wrap, qc, runId]);
  return { answer, skip, finish, busy };
}

/**
 * What each level would do to this resume for this job (ticket 22).
 *
 * Keyed on the posting *and* the source document, because the answer
 * describes one against the other — a key on the job alone would keep
 * showing the previous resume's roles after the source is switched, which is
 * the one way this feature could quietly lie.
 *
 * `staleTime` is long: the answer moves only when the resume is edited
 * (it reads no Skill bank since skill-bank-internal-only ticket 03), and it
 * costs the server no model call either way.
 */
export const useTailorPreview = (jobId: string | null | undefined, source?: { resumeId?: string; uploadId?: string }) =>
  useQuery({
    queryKey: qk.tailorPreview(jobId ?? '', source?.resumeId ?? source?.uploadId ?? ''),
    queryFn: () => tailorApi.preview(jobId!, source),
    enabled: !!jobId,
    staleTime: 60_000,
  });

/** Everything a finished run may have changed: the job's `tailored`, the library, credits, history, and the job's row in Action. */
export function invalidateAfterRun(qc: QueryClient, run: TailorRun) {
  const ps: Promise<unknown>[] = [invalidateCredits(qc), qc.invalidateQueries({ queryKey: ['resumes'] }), qc.invalidateQueries({ queryKey: qk.tailorHistory })];
  if (run.jobId) ps.push(refreshAction(qc));
  if (run.jobId) ps.push(qc.invalidateQueries({ queryKey: qk.job(run.jobId) }));
  if (run.outputResumeId) ps.push(qc.invalidateQueries({ queryKey: qk.resume(run.outputResumeId) }), qc.invalidateQueries({ queryKey: qk.resumeHealth(run.outputResumeId) }));
  return Promise.all(ps);
}

/**
 * "+ skill" on a job page (ux-bug-pass ticket 25): the skill goes into this
 * job's tailored résumé, or waits for its next Tailor run — never the master
 * and never the Skill bank, so nothing account-wide is invalidated. Not
 * optimistic, like every skill write: the job is refetched and its
 * `tailored.scoreAfter`/`tailoredProjected` are the server's.
 */
export function useJobSkillAdd(jobId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: JobSkillAddRequest) => jobsApi.addSkill(jobId, body),
    onSuccess: (res) => {
      const ps: Promise<unknown>[] = [qc.invalidateQueries({ queryKey: qk.job(jobId) })];
      if (res.tailored) ps.push(qc.invalidateQueries({ queryKey: qk.resume(res.tailored.resumeId) }), qc.invalidateQueries({ queryKey: ['resumes'] }));
      return Promise.all(ps);
    },
  });
}

// ── Action's apply kit (action-tab ticket 19) ────────────────────────────
/**
 * One row's apply kit, `GET /v1/action/{jobId}/kit`. Read on every opening and not kept (`gcTime: 0`): the form
 * and the bank can change between two openings, and the kit the person copies from, and later records as sent,
 * must be the one just read. `jobId: null` reads nothing (no kit is open).
 */
export const useActionKit = (jobId: string | null) =>
  useQuery({ queryKey: qk.actionKit(jobId ?? ''), queryFn: () => actionApi.kit(jobId!), enabled: !!jobId, staleTime: 0, gcTime: 0 });
/**
 * The first Apply ↗ click (`POST …/opened`). Fire and forget: a failure is not shown and never holds the sheet.
 * It re-reads the list alone (`exact`, not the kits under the same prefix) so the row can say "Opened · did you apply?".
 */
export function useActionOpened() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (jobId: string) => actionApi.opened(jobId), onSuccess: () => void qc.invalidateQueries({ queryKey: qk.action, exact: true }) });
}

// ── applications ─────────────────────────────────────────────────────────
/** `enabled` false asks for nothing: Career Compass with the server flag on draws its chart from the payload's weeks (career-compass-metrics ticket 10). */
export const useApplications = (enabled = true) => useQuery({ queryKey: qk.applications, queryFn: listAllApplications, staleTime: 15_000, enabled });
export const useManualWeekly = () => useQuery({ queryKey: qk.manualWeekly, queryFn: applicationsApi.manualWeekly, staleTime: 5 * 60_000 });

/** The `id` of an application the server has not answered for yet (ux-bug-pass-3 ticket 02). Nothing may be sent against it. */
export const OPTIMISTIC_APPLICATION_ID = 'optimistic';

/**
 * No handler here returns its refresh. TanStack Query awaits a promise returned
 * from a mutation's `onSuccess`/`onSettled` before the mutation settles, so
 * returning one kept `isPending` true, and the caller's own `onSuccess` unrun,
 * until the applications list, Career Compass and the job's detail (the read
 * that recomputes the match) had all come back (ux-bug-pass-3 ticket 02). The
 * server's answer is written into the cache instead, and the reads follow.
 */
export function useApplicationMutations() {
  const qc = useQueryClient();
  const refresh = (jobId?: string | null) => {
    void qc.invalidateQueries({ queryKey: qk.applications });
    void qc.invalidateQueries({ queryKey: qk.insights });
    // The deck leaves a tracked job out.
    // The held deck is marked stale, not re-read here: it is read again (the first batch) when the deck screen next mounts.
    void qc.invalidateQueries({ queryKey: qk.deck });
    // A tracked job's row in Action is `sent`, and one no longer tracked is not.
    void refreshAction(qc);
    if (jobId) void qc.invalidateQueries({ queryKey: qk.job(jobId) });
  };
  /** The job page's record of this application, as the cached job detail holds it. */
  const setJobApplication = (jobId: string, next: (d: JobDetailResponse) => JobApplicationRef | null) =>
    qc.setQueryData<JobDetailResponse>(qk.job(jobId), (d) => (d ? { ...d, application: next(d) } : d));
  const create = useMutation({
    mutationFn: (body: ApplicationCreateRequest) => applicationsApi.create(body),
    // Optimistic on a job page: "Yes, I applied" shows the applied state before
    // any response. A read of the job already in flight would land after this
    // write and say "not applied", so it is cancelled first.
    onMutate: async (body) => {
      if (!body.jobId) return;
      await qc.cancelQueries({ queryKey: qk.job(body.jobId) });
      setJobApplication(body.jobId, (d) => d.application ?? {
        id: OPTIMISTIC_APPLICATION_ID, status: 'applied', appliedAt: body.appliedAt ?? new Date().toISOString(),
        resume: d.tailored && d.tailored.resumeId === body.resumeId ? { id: d.tailored.resumeId, name: d.tailored.name, tailored: true } : null,
      });
    },
    // Only the optimistic record is taken back: a read that landed meanwhile is the server's and stays.
    onError: (_e, body) => { if (body.jobId) setJobApplication(body.jobId, (d) => (d.application?.id === OPTIMISTIC_APPLICATION_ID ? null : d.application)); },
    onSuccess: (app, body) => {
      if (body.jobId) setJobApplication(body.jobId, () => ({ id: app.id, status: app.status, appliedAt: app.appliedAt, resume: app.resume }));
      qc.setQueryData<Application[]>(qk.applications, (list) => (list ? [app, ...list.filter((a) => a.id !== app.id)] : list));
      refresh(body.jobId);
    },
  });
  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: ApplicationStatus }) => applicationsApi.setStatus(id, status),
    // Optimistic: the status select should feel instant; the server copy replaces it on settle.
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: qk.applications });
      const prev = qc.getQueryData<Application[]>(qk.applications);
      if (prev) qc.setQueryData<Application[]>(qk.applications, prev.map((a) => (a.id === id ? { ...a, status, statusChangedAt: new Date().toISOString() } : a)));
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(qk.applications, ctx.prev); },
    onSettled: () => refresh(),
  });
  const remove = useMutation({
    mutationFn: ({ id }: { id: string; jobId?: string | null }) => applicationsApi.remove(id),
    onSuccess: (_r, v) => {
      if (v.jobId) setJobApplication(v.jobId, (d) => (d.application?.id === v.id ? null : d.application));
      qc.setQueryData<Application[]>(qk.applications, (list) => list?.filter((a) => a.id !== v.id));
      refresh(v.jobId);
    },
  });
  return { create, setStatus, remove };
}

// ── auto apply ───────────────────────────────────────────────────────────
export const useAutoApply = () => useQuery({ queryKey: qk.autoApply, queryFn: autoApplyApi.get, staleTime: 15_000 });
export function useAutoApplyMutations() {
  const qc = useQueryClient();
  const set = (state: Awaited<ReturnType<typeof autoApplyApi.get>>) => qc.setQueryData(qk.autoApply, state);
  const saveStrategy = useMutation({ mutationFn: (s: AutoApplyStrategy) => autoApplyApi.saveStrategy(s), onSuccess: set });
  const toggle = useMutation({ mutationFn: (enabled: boolean) => autoApplyApi.toggle(enabled), onSuccess: (s) => { set(s); invalidateCredits(qc); } });
  return { saveStrategy, toggle };
}

// ── the profile (profile-progress ticket 01) ─────────────────────────────
/** Always read fresh when the page opens: the figure is computed on the server and moves with any write, the questions sheet's included. */
export const useProfile = () => useQuery({ queryKey: qk.profile, queryFn: profileApi.read, staleTime: 0 });
/** Decline a row's offer from the résumé; only the profile is read again, since the bank is untouched. */
export function useProfileKeepMine() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (field: string) => profileApi.keepMine(field), onSuccess: () => qc.invalidateQueries({ queryKey: qk.profile }) });
}

/** Names that complete what is typed into the skill tags (`GET /v1/profile/skills`); nothing is asked for an empty box. */
export function useSkillSuggestions(q: string) {
  // The names are asked a moment after the last key, not on each one (the route is rate limited, and the list is short).
  const [settled, setSettled] = useState('');
  useEffect(() => { const t = setTimeout(() => setSettled(q.trim().toLowerCase()), SKILL_SUGGEST_DELAY_MS); return () => clearTimeout(t); }, [q]);
  return useQuery({ queryKey: ['profile', 'skills', settled] as const, queryFn: () => profileApi.skills(settled), enabled: settled.length > 0, staleTime: 5 * 60_000 });
}
export const SKILL_SUGGEST_DELAY_MS = 250;

/** Say a row does not apply, or take that back. Counts toward the figure and changes nothing a form reads, so only the profile is read again. */
export function useProfileNotApplicable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { field: string; applies: boolean }) => (v.applies ? profileApi.clearNotApplicable(v.field) : profileApi.markNotApplicable(v.field)),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.profile }),
  });
}
/** Save one profile row (`source: 'suggested'` for a derived fact confirmed untouched). A write moves the figure, the bank's list and what Action's rows still ask, so all three are read again. */
export function useProfileWrite() {
  const qc = useQueryClient();
  const reread = () => { void qc.invalidateQueries({ queryKey: qk.action }); void qc.invalidateQueries({ queryKey: qk.answers }); return qc.invalidateQueries({ queryKey: qk.profile }); };
  return useMutation({ mutationFn: (v: { field: string; value: AnswerValue; source?: 'user' | 'suggested'; place?: ProfileLocationPut; values?: string[] }) => (v.values ? profileApi.putValues(v.field, { values: v.values }) : v.place ? profileApi.putLocation(v.place) : profileApi.put(v.field, { value: v.value, source: v.source ?? 'user' })), onSuccess: reread });
}

// ── the tailoring level (ux-bug-pass-6 ticket 08, gap 47) ────────────────
/** The `profile_fields` key the level is written under, through the profile's own row write. */
export const TAILOR_DEFAULT_FIELD = 'tailor_default_level';
/** The account's tailoring level, off the profile read (`match` until one is saved). A minute fresh: the Job Queue chip need not re-read the profile on every visit. */
export const useTailorDefaultLevel = () => useQuery({ queryKey: qk.profile, queryFn: profileApi.read, staleTime: 60_000, select: (p: ProfileResponse) => p.tailorDefaultLevel });
/** Save the level. Applies from the next job: nothing already queued is read again or redone, so only the profile's copy is updated in place. */
export function useSaveTailorDefaultLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (level: TailorDefaultLevel) => profileApi.put(TAILOR_DEFAULT_FIELD, { value: { v: level }, source: 'user' }),
    onSuccess: (_written, level) => { qc.setQueryData<ProfileResponse>(qk.profile, (p) => (p ? { ...p, tailorDefaultLevel: level } : p)); },
  });
}

// ── the first-swipe receipt's "seen" flag (ux-bug-pass-6 ticket 09, gap 48) ──
/** The `profile_fields` key the flag is written under; the server stamps its own time. */
export const RECEIPT_SEEN_FIELD = 'first_swipe_receipt_seen_at';
/** When the account first saw the receipt, off the profile read; null until then. */
export const useFirstSwipeReceiptSeen = () => useQuery({ queryKey: qk.profile, queryFn: profileApi.read, staleTime: 60_000, select: (p: ProfileResponse) => p.firstSwipeReceiptSeenAt });
/** Mark the receipt seen, once it has rendered. Only a write that succeeded marks the profile's copy. */
export function useMarkReceiptSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => profileApi.put(RECEIPT_SEEN_FIELD, { value: { v: 'seen' }, source: 'user' }),
    onSuccess: () => { qc.setQueryData<ProfileResponse>(qk.profile, (p) => (p ? { ...p, firstSwipeReceiptSeenAt: p.firstSwipeReceiptSeenAt ?? new Date().toISOString() } : p)); },
  });
}

export const WAITLIST_FIELD = 'auto_apply_waitlist_joined_at';
/** When the account joined the Auto-Apply waitlist (ux-bug-pass-7 ticket 18), off the profile read; null until then. */
export const useAutoApplyWaitlist = () => useQuery({ queryKey: qk.profile, queryFn: profileApi.read, staleTime: 0, select: (p: ProfileResponse) => p.autoApplyWaitlistJoinedAt });
/** Join the waitlist. Only a write that succeeded marks the profile's copy; a repeat keeps the first time. */
export function useJoinAutoApplyWaitlist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => profileApi.put(WAITLIST_FIELD, { value: { v: 'joined' }, source: 'user' }),
    onSuccess: () => { qc.setQueryData<ProfileResponse>(qk.profile, (p) => (p ? { ...p, autoApplyWaitlistJoinedAt: p.autoApplyWaitlistJoinedAt ?? new Date().toISOString() } : p)); },
  });
}

// ── the Job Queue's first-visit story (ux-bug-pass-8 ticket 09, gap 53) ──
export const STORY_SEEN_FIELD = 'job_queue_story_seen_at';
/** When the account was shown the Job Queue story, off the profile read; null until then, undefined while the read is out. */
export const useJobQueueStorySeen = () => useQuery({ queryKey: qk.profile, queryFn: profileApi.read, staleTime: 60_000, select: (p: ProfileResponse) => p.jobQueueStorySeenAt });
/** Mark the story seen, once card 1 has drawn. Only a write that succeeded marks the profile's copy; a failed one only means the story shows again next visit. */
export function useMarkJobQueueStorySeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => profileApi.put(STORY_SEEN_FIELD, { value: { v: 'seen' }, source: 'user' }),
    onSuccess: () => { qc.setQueryData<ProfileResponse>(qk.profile, (p) => (p ? { ...p, jobQueueStorySeenAt: p.jobQueueStorySeenAt ?? new Date().toISOString() } : p)); },
  });
}

// ── the first-shortlist preferences sheet (ux-bug-pass-8 ticket 06) ─────
export const PREFS_ASKED_FIELD = 'shortlist_prefs_asked_at';
/** What the first-shortlist sheet reads off the profile: whether it has asked, the account country (the loader's count), and the job types row. */
export const useShortlistPrefsProfile = (enabled: boolean) => useQuery({
  queryKey: qk.profile, queryFn: profileApi.read, staleTime: 60_000, enabled,
  select: (p: ProfileResponse) => ({ askedAt: p.shortlistPrefsAskedAt, country: p.accountCountry.name, jobTypes: p.fields.find((f) => f.id === 'job_types') ?? null }),
});
/**
 * The sheet's one save: the places and arrangements (`PUT /v1/deck/requirements`, skipped when `requirements` is
 * null), the job types (the profile's list row) and the asked flag, in that order. Any failure rejects and the
 * caller keeps the sheet open; a retry sends all of it again (each write is a repeat-safe put). Only a save that
 * succeeded marks the profile's copy. The deck is not read here: the caller reads it behind its loader.
 */
export function useSaveShortlistPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { requirements: DeckRequirementsPut | null; jobTypes: string[] }) => {
      if (v.requirements) qc.setQueryData(qk.deckRequirements, await deckApi.putRequirements(v.requirements));
      await profileApi.putValues('job_types', { values: v.jobTypes });
      await profileApi.put(PREFS_ASKED_FIELD, { value: { v: 'asked' }, source: 'user' });
    },
    onSuccess: () => {
      qc.setQueryData<ProfileResponse>(qk.profile, (p) => (p ? { ...p, shortlistPrefsAskedAt: p.shortlistPrefsAskedAt ?? new Date().toISOString() } : p));
      void qc.invalidateQueries({ queryKey: qk.profile });
    },
  });
}
/** "Skip": the flag alone. A failed write only means the sheet asks again next visit; the deck loads either way. */
export function useSkipShortlistPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => profileApi.put(PREFS_ASKED_FIELD, { value: { v: 'asked' }, source: 'user' }),
    onSuccess: () => { qc.setQueryData<ProfileResponse>(qk.profile, (p) => (p ? { ...p, shortlistPrefsAskedAt: p.shortlistPrefsAskedAt ?? new Date().toISOString() } : p)); },
  });
}
/** Read the deck again from its first batch, for the loader after a save. Settles either way: the deck shows its own error. */
export function useReloadDeck() {
  const qc = useQueryClient();
  return useCallback(() => qc.refetchQueries({ queryKey: qk.deck, exact: true }).catch(() => undefined), [qc]);
}

// ── Account → Job preferences (ux-bug-pass-8 ticket 12) ─────────────────
/**
 * The page's one save, through the routes that exist: the target role (the profile's row), the places and
 * arrangements (`PUT /v1/deck/requirements`, the first-shortlist sheet's rule) and the job types (the profile's list
 * row); a null part is not written. Any failure rejects with the rest unsent. Settled either way, the profile, the
 * requirements and the deck are read again, so the next deck batch is the new one.
 */
export function useSaveJobPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { role: string | null; requirements: DeckRequirementsPut | null; jobTypes: string[] | null }) => {
      if (v.role) await profileApi.put('target_role', { value: { v: v.role }, source: 'user' });
      if (v.requirements) qc.setQueryData(qk.deckRequirements, await deckApi.putRequirements(v.requirements));
      if (v.jobTypes) await profileApi.putValues('job_types', { values: v.jobTypes });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: qk.profile });
      void qc.invalidateQueries({ queryKey: qk.deckRequirements });
      void qc.invalidateQueries({ queryKey: qk.deck });
    },
  });
}

// ── the account's country (ux-bug-pass-7 ticket 12, ADR 0031) ────────────
export const ACCOUNT_COUNTRY_FIELD = 'account_country';
/** Set the country the account is matched in. The server marks every stored score stale and rescores; here only the profile is read again. */
export function useSaveAccountCountry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => profileApi.put(ACCOUNT_COUNTRY_FIELD, { value: { v: name }, source: 'user' }),
    onSuccess: (_written, name) => {
      qc.setQueryData<ProfileResponse>(qk.profile, (p) => (p ? { ...p, accountCountry: { ...p.accountCountry, name, chosen: true } } : p));
      void qc.invalidateQueries({ queryKey: qk.profile });
    },
  });
}

// ── the answer bank (action-tab ticket 21: Account → Your answers) ───────
export const useAnswers = () => useQuery({ queryKey: qk.answers, queryFn: answersApi.list, staleTime: 15_000 });

/**
 * Edit and delete for one banked answer. A save is the person's own from then on (`source: 'user'`), so an
 * edited seed stops following the résumé. Both writes change what Action's rows still ask: the list, every kit
 * and the questions sit under `qk.action`'s prefix, so one invalidation reaches all three and the nav's badge.
 * Unlike the application mutations, these return the bank's refetch, so a write settles only once the list is
 * the server's: the row never shows the old value, or a deleted answer, after its form has closed.
 */
export function useAnswerMutations() {
  const qc = useQueryClient();
  const reread = () => { void qc.invalidateQueries({ queryKey: qk.action }); return qc.invalidateQueries({ queryKey: qk.answers }); };
  const save = useMutation({ mutationFn: (v: { answerKey: string; value: AnswerValue }) => answersApi.put(v.answerKey, { value: v.value, source: 'user' }), onSuccess: reread });
  const remove = useMutation({ mutationFn: (answerKey: string) => answersApi.remove(answerKey), onSuccess: reread });
  return { save, remove };
}

// ── Action's questions sheet (action-tab ticket 18) ──────────────────────
/** Every open question, read fresh. The sheet walks that list as it was when it opened and reads it again only to see what is left at the end, so this is a fetch and not an observer: a write that moves the rows does not move the sheet's list. */
export const fetchActionQuestions = (qc: QueryClient) => qc.fetchQuery({ queryKey: qk.actionQuestions, queryFn: actionApi.questions, staleTime: 0 });

/**
 * One answer from the sheet (spec 9): a banked question is `PUT /v1/answers/{answerKey}` with where the answer came
 * from, a job-scoped one `PUT /v1/action/{jobId}/answers/{qhash}`. It writes at once, so closing the sheet keeps
 * what was given. Either returns the jobIds whose state moved; Action's rows (and the badge) are read again, and
 * they flash together on the screen, so the sheet itself does nothing more.
 */
export function useQuestionAnswerWrite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ question: q, value, source }: { question: ActionQuestion; value: AnswerValue; source?: 'user' | 'suggested' }) =>
      q.answerKey ? answersApi.put(q.answerKey, { value, source }) : actionApi.answerForJob(q.jobId ?? '', q.qhash, value),
    onSuccess: () => { void refreshAction(qc); void qc.invalidateQueries({ queryKey: qk.answers }); },
  });
}

/**
 * `POST /v1/action/{jobId}/suggest` (spec 7.4): proposals for a job's open questions that are neither sensitive nor a
 * textarea. A model call, so the sheet asks once per job per opening, when it first reaches such a question; a
 * failure is not shown (the question is asked plainly). Nothing is written until the person confirms one.
 */
export const useActionSuggest = () => useMutation({ mutationFn: (jobId: string) => actionApi.suggest(jobId) });

// ── insights ─────────────────────────────────────────────────────────────
export const useInsights = () => useQuery({ queryKey: qk.insights, queryFn: insightsApi.get, staleTime: 60_000 });
/** Saves Compass settings (ticket 09). The server refreshes in the request and
 *  answers with the whole page, so the answer replaces the cached one: the new
 *  pace and score show with the save, no refetch. */
export function useSaveCompassSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: insightsApi.putSettings,
    onSuccess: (data) => qc.setQueryData<Insights>(qk.insights, data),
  });
}
/** Tells the server the Weekly review was shown expanded (ticket 11). The
 *  cached page is left alone on purpose: the card stays open while it is being
 *  read, and the next fetch brings the one-line "Last week". */
export const useMarkReviewSeen = () => useMutation({ mutationFn: insightsApi.reviewSeen });
/** "Any replies this week?" (career-compass-metrics ticket 12): a status tap
 *  (the tracker's own PATCH), its Undo and "Not yet". Each reads the page and
 *  the tracker again once it has answered; what moves the funnel at once is the
 *  screen's pending overlay (`features/insights/compass/pending.ts`), because
 *  the server's funnel waits for the next snapshot refresh. */
export function useStatusPromptWrites() {
  const qc = useQueryClient();
  const reread = () => {
    void qc.invalidateQueries({ queryKey: qk.insights });
    void qc.invalidateQueries({ queryKey: qk.applications });
  };
  const tap = useMutation({ mutationFn: (v: { id: string; status: ApplicationStatus }) => applicationsApi.setStatus(v.id, v.status), onSettled: reread });
  const undo = useMutation({ mutationFn: (id: string) => applicationsApi.undoStatus(id), onSettled: reread });
  const snooze = useMutation({ mutationFn: (id: string) => applicationsApi.snoozePrompt(id), onSettled: reread });
  return { tap, undo, snooze };
}

/**
 * "Get certified" (ux-bug-pass-7 ticket 20): moves one certification's step in the cached page at once, puts it back
 * if the server refuses, and keeps the server's step when it answers. The cards live in the insights payload, so the
 * cache written is that one; a refetch in flight is cancelled first so it cannot draw the old step over the new one.
 */
export function useCertificationStep() {
  const qc = useQueryClient();
  const setStep = (id: string, step: CertificationStepValue) => {
    const now = qc.getQueryData<Insights>(qk.insights);
    if (now) qc.setQueryData<Insights>(qk.insights, { ...now, certifications: now.certifications.map((c) => (c.id === id ? { ...c, step } : c)) });
  };
  return useMutation({
    mutationFn: (v: { id: string; step: CertificationStepValue }) => certificationsApi.setStep(v.id, v.step),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: qk.insights });
      const before = qc.getQueryData<Insights>(qk.insights);
      setStep(v.id, v.step);
      return { before };
    },
    onError: (_e, _v, ctx) => { if (ctx?.before) qc.setQueryData(qk.insights, ctx.before); },
    onSuccess: (saved) => { if (saved?.id) setStep(saved.id, saved.step); },
  });
}

// ── learning path ────────────────────────────────────────────────────────
/** While the server is still building the path (`state: building`) the tab asks again this often. */
export const LEARNING_POLL_MS = 3000;
/** The longest the tab keeps asking while the answer is still `building` (go-live ticket 04); then it shows a notice. */
export const LEARNING_BUILD_WAIT_MS = 60_000;
/**
 * `enabled` is the tab being open: the first read of a master résumé with no path
 * starts its build on the server, so nothing asks before the person looks.
 * The interval answers `false` once `state` is anything but `building` (and after
 * an error), and a query with no observer is not polled, so unmounting stops it.
 * It also answers `false` once the tab has waited `LEARNING_BUILD_WAIT_MS` on
 * `building`; `waitedOut` is then true and stays so until the tab is left or the
 * state changes, so the next visit asks again.
 */
export const useLearningPath = (enabled = true) => {
  const [waitedOut, setWaitedOut] = useState(false);
  const gaveUp = useRef(false);
  const query = useQuery({
    queryKey: qk.learningPath,
    queryFn: learningApi.get,
    enabled,
    staleTime: 30_000,
    refetchInterval: (q) => (q.state.status !== 'error' && !gaveUp.current && isLearningPathBuilding(q.state.data) ? LEARNING_POLL_MS : false),
  });
  const waiting = enabled && isLearningPathBuilding(query.data);
  useEffect(() => {
    if (!waiting) { gaveUp.current = false; setWaitedOut(false); return undefined; }
    const timer = setTimeout(() => { gaveUp.current = true; setWaitedOut(true); }, LEARNING_BUILD_WAIT_MS);
    return () => clearTimeout(timer);
  }, [waiting]);
  return { ...query, waitedOut };
};

/**
 * Moves one item's step in the cached path at once (the dot moves before the server answers, D19), puts it back if
 * the server refuses, and takes the server's item when it answers, so `updatedAt` is the stored one.
 */
export function useLearningItemStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; status: LearningItemSet }) => learningApi.setStatus(v.id, v.status),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: qk.learningPath });
      const before = qc.getQueryData<LearningPath>(qk.learningPath);
      // `updatedAt` moves with the dot only when the step changes, as the server does.
      if (before) qc.setQueryData<LearningPath>(qk.learningPath, { ...before, items: before.items.map((i) => (i.id === v.id && i.status !== v.status ? { ...i, status: v.status, updatedAt: new Date().toISOString() } : i)) });
      return { before };
    },
    onError: (_e, _v, ctx) => { if (ctx?.before) qc.setQueryData(qk.learningPath, ctx.before); },
    onSuccess: (saved) => {
      const now = qc.getQueryData<LearningPath>(qk.learningPath);
      if (now && saved?.id) qc.setQueryData<LearningPath>(qk.learningPath, { ...now, items: now.items.map((i) => (i.id === saved.id ? saved : i)) });
    },
  });
}

/**
 * A person's own items (ux-bug-pass-6 ticket 11). Add and Undo wait for the server (it makes the id); a step moves the
 * dot at once and goes back if the save fails; Remove takes the row out at once and puts it back if the save fails.
 */
const patchOwn = (qc: ReturnType<typeof useQueryClient>, fn: (own: LearningOwnItem[]) => LearningOwnItem[]) => {
  const now = qc.getQueryData<LearningPath>(qk.learningPath);
  if (now) qc.setQueryData<LearningPath>(qk.learningPath, { ...now, ownItems: fn(now.ownItems ?? []) });
};
export function useLearningOwnAdd() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: LearningOwnAdd) => learningApi.addOwn(v),
    onSuccess: (saved) => patchOwn(qc, (own) => [...own.filter((i) => i.id !== saved.id), saved]),
  });
}
export function useLearningOwnStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; status: LearningItemSet }) => learningApi.setOwnStatus(v.id, v.status),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: qk.learningPath });
      const before = qc.getQueryData<LearningPath>(qk.learningPath);
      patchOwn(qc, (own) => own.map((i) => (i.id === v.id && i.status !== v.status ? { ...i, status: v.status, updatedAt: new Date().toISOString() } : i)));
      return { before };
    },
    onError: (_e, _v, ctx) => { if (ctx?.before) qc.setQueryData(qk.learningPath, ctx.before); },
    onSuccess: (saved) => { if (saved?.id) patchOwn(qc, (own) => own.map((i) => (i.id === saved.id ? saved : i))); },
  });
}
export function useLearningOwnRemove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string }) => learningApi.removeOwn(v.id),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: qk.learningPath });
      const before = qc.getQueryData<LearningPath>(qk.learningPath);
      patchOwn(qc, (own) => own.filter((i) => i.id !== v.id));
      return { before };
    },
    onError: (_e, _v, ctx) => { if (ctx?.before) qc.setQueryData(qk.learningPath, ctx.before); },
  });
}

// ── guest ────────────────────────────────────────────────────────────────
/**
 * `opts.enabled` is a second way to pause the poll, beside `id`/`sample` —
 * resume-360 ticket 37's 2-minute timeout uses it to stop polling a run the
 * client has given up on, without touching the ready/failed → `false` logic
 * below (which keeps working on its own once a result lands).
 */
export const useGuestAnalysis = (id: string | null | undefined, sample: boolean, opts?: { enabled?: boolean }) =>
  useQuery({
    queryKey: sample ? ['guest', 'analysis', 'sample'] : qk.guestAnalysis(id ?? ''),
    queryFn: () => (sample ? guestApi.sampleAnalysis() : guestApi.analysis(id!)),
    enabled: (sample || !!id) && (opts?.enabled ?? true),
    // A poll that has errored stays errored until "Try again" refetches it.
    // TanStack clears the error when the next fetch starts, so an interval
    // left running swaps the error and its button for a checklist that reads
    // as still working, every 1.5 s.
    refetchInterval: (q) => {
      if (q.state.status === 'error' && !resumesAfterGap(q.state)) return false;
      const d = q.state.data;
      return d && (d.status === 'ready' || d.status === 'failed') ? false : 1500;
    },
    retry: (count, err) => (isGapError(err) ? count < GAP_RETRIES : count < 2 && !(err && (err as { status?: number }).status === 404)),
    retryDelay: (count) => gapRetryDelay(count),
  });

/**
 * Ticket 38: the before/after screenshot render's own poll, split from
 * `useGuestAnalysis` because the two stop conditions are independent —
 * `status` can reach `ready` (report shown, text cards drawn) while
 * `previewImages` is still `pending` (the screenshots are a progressive
 * enhancement, never a gate on the report). Same query key and `queryFn` as
 * `useGuestAnalysis`, so the two share one cached response and one
 * in-flight request whenever both are polling at once — this is a second
 * *observer*, not a second endpoint. Reads nothing when the field is absent
 * (no render jobs for this analysis: a pre-ticket-38 row, or the sample,
 * which never carries one), so it polls nothing it has no evidence will
 * ever arrive.
 *
 * ux-bug-pass ticket 08: a 429 (two polls in one 1 s bucket on a jittery
 * connection), a 5xx (an api restart on deploy) or a dropped request is
 * retried with backoff, honouring `Retry-After`. Once the report is drawn, an
 * error that outlasts the retries only slows the poll to 5 s while a render
 * is still pending; it never stops it for good (that left the text card up
 * with the image never requested). Before the report is drawn, the analyzing
 * screen owns the error and its "Try again" (`useGuestAnalysis`), so it stops.
 */
const transient = (err: unknown) => !isApiError(err) || err.status === 429 || err.status >= 500;
export const useGuestPreviewImages = (id: string | null | undefined, sample: boolean, opts?: { enabled?: boolean }) =>
  useQuery({
    queryKey: sample ? ['guest', 'analysis', 'sample'] : qk.guestAnalysis(id ?? ''),
    queryFn: () => (sample ? guestApi.sampleAnalysis() : guestApi.analysis(id!)),
    enabled: (sample || !!id) && (opts?.enabled ?? true),
    refetchInterval: (q) => {
      const d = q.state.data;
      const imgs = d?.previewImages;
      const pending = !!imgs && (imgs.before === 'pending' || imgs.after === 'pending');
      if (q.state.status === 'error') return d?.status === 'ready' && pending && transient(q.state.error) ? 5000 : false;
      return pending ? 1500 : false;
    },
    retry: (count, err) => count < 3 && transient(err),
    retryDelay: (count, err) => (isApiError(err) && err.retryAfterSeconds ? err.retryAfterSeconds * 1000 : Math.min(1000 * 2 ** count, 8000)),
  });
